import { useMemo } from 'react';
import { useApiQuery } from '@/hooks/useApiQuery';
import { keycloakApi } from '@/api/keycloak';
import type { KeycloakGroup } from '@/api/keycloak';
import mockData from '@/data/mock-data.json';

interface AccessGroup {
  id: string;
  name: string;
  description: string;
  memberCount: number;
  members: string[];
  createdAt: string;
}
interface ListGroupsResponse {
  groups: KeycloakGroup[];
}

interface UseKeycloakGroupsResult {
  groups: AccessGroup[];
  isLoading: boolean;
  error: string | undefined;
  isUsingMockData: boolean;
  reload: () => void;
}

const mapKeycloakGroupToAccessGroup = (group: KeycloakGroup): AccessGroup => ({
  id: group.id,
  name: group.name,
  description: group.description ?? '',
  memberCount: group.members?.length ?? 0,
  members: group.members?.map((m) => m.userId) ?? [],
  createdAt: new Date().toISOString(),
});

const mockFallback: ListGroupsResponse = {
  groups: [],
};

export const useKeycloakGroups = (): UseKeycloakGroupsResult => {
  const { data, isLoading, error, isUsingMockData, reload } =
    useApiQuery<ListGroupsResponse>(
      {
        queryFn: (signal) => {
          void signal;
          return keycloakApi.listGroups();
        },
        mockData: mockFallback,
      },
      [],
    );

  const groups = useMemo(() => {
    if (isUsingMockData) {
      return mockData.accessGroups as AccessGroup[];
    }
    return data?.groups.map(mapKeycloakGroupToAccessGroup) ?? [];
  }, [data, isUsingMockData]);

  return { groups, isLoading, error, isUsingMockData, reload };
};
